// Auth Provider component for initializing authentication

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { validateTokensOnStartup, setupTokenRefreshInterval } from '@/services/api/interceptor';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { tokens, isAuthenticated, setLoading, setAuthReady, logout } = useAuthStore();

  // Validate stored auth on mount — prevents stale tokens from triggering API calls.
  // Always validates with the server because client-side JWT checks are insufficient
  // (tokens may be invalidated server-side via key rotation, session revocation, etc.)
  useEffect(() => {
    if (!isAuthenticated || !tokens?.accessToken) {
      // No stored auth — nothing to validate, safe to mark ready
      setAuthReady(true);
      return;
    }

    // Validate tokens with the server before allowing any data queries
    setLoading(true);
    validateTokensOnStartup()
      .then(() => {
        setLoading(false);
        setAuthReady(true);
      })
      .catch(() => {
        // Server rejected the tokens — clear stale auth
        logout();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Set up periodic token refresh only when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const cleanup = setupTokenRefreshInterval();
    return cleanup;
  }, [isAuthenticated]);

  return <>{children}</>;
};

export default AuthProvider;
